import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faVideo, faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons";
import getCroppedImg from "./cropImage"; // helper you already have

function getFileNameFromUrl(url) {
  if (!url) return "";
  const name = url.split('/').pop().split('?')[0];
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
}

function ImageUploaderWithCrop({ aspectRatio, onImageCropped, recommendedResolution, templateType, currentMedia, fieldId = "fileUpload" }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const uploadedFileName = currentMedia?.file?.name || getFileNameFromUrl(currentMedia?.url);
  const MAX_IMAGE_SIZE_MB = templateType === "richcardcarousel" ? 1 : 2;
  const MAX_VIDEO_SIZE_MB = templateType === "richcardcarousel" ? 5 : 10;



  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/avi', 'video/mov', 'video/mkv', 'video/flv', 'video/webm', 'video/wmv'];

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = null; // <-- reset input value

    setUploadError(""); // clear old errors

    if (!file) return;

    const fileType = file.type;
    const fileSizeMB = file.size / (1024 * 1024);
    const fileUrl = URL.createObjectURL(file);

    if (fileType.startsWith("image/")) {
      if (!ALLOWED_IMAGE_TYPES.includes(fileType)) {
        setUploadError("Invalid image type. Allowed: JPG, JPEG, PNG, GIF.");
        return;
      }
      if (fileSizeMB > MAX_IMAGE_SIZE_MB) {
        setUploadError(`Image must be less than ${MAX_IMAGE_SIZE_MB} MB.`);
        return;
      }
      setZoom(1);
      setMinZoom(1);
      setCrop({ x: 0, y: 0 });
      setOriginalImageFile(file);
      setImageSrc(fileUrl);
      setShowModal(true);
    } else if (fileType.startsWith("video/")) {
      if (!ALLOWED_VIDEO_TYPES.includes(fileType)) {
        setUploadError("Invalid video type. Allowed: mp4, avi, mov, mkv, flv, webm, wmv.");
        return;
      }
      if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
        setUploadError(`Video must be less than ${MAX_VIDEO_SIZE_MB} MB.`);
        return;
      }

      if (onImageCropped) {
        onImageCropped(fileUrl, file, file);
      }
    } else {
      setUploadError("Unsupported file type.");
    }
  };




  const showCroppedImage = useCallback(async () => {
    try {
      if (!originalImageFile) {
        throw new Error("Original file missing");
      }
      const { blob, file } = await getCroppedImg(imageSrc, croppedAreaPixels, originalImageFile.name);
      const blobUrl = URL.createObjectURL(blob);
      setShowModal(false);
      if (onImageCropped) {
        onImageCropped(blobUrl, blob, file);
      }
    } catch (e) {
      console.error("Cropping failed", e);
    }
  }, [imageSrc, croppedAreaPixels]);

  return (
    <div>
      <label htmlFor={fieldId} className="form-label d-block">
        Upload Public Image/Video
        <span className="text-muted ml-1 small">(File name must contain only letters A–Z)</span>
      </label>

      <div className="upload-dropzone">
        <div className="upload-thumb">
          {uploadedFileName ? (
            currentMedia?.file_type === "video" ? (
              <FontAwesomeIcon icon={faVideo} className="text-secondary" />
            ) : (
              <img src={currentMedia?.url} alt="Uploaded preview" className="upload-thumb-media" />
            )
          ) : (
            <FontAwesomeIcon icon={faImage} className="text-muted" />
          )}
        </div>

        <div className="upload-info">
          <div className={uploadedFileName ? "text-success small text-truncate" : "text-muted small"}>
            {uploadedFileName ? `✓ ${uploadedFileName}` : "No file selected"}
          </div>
          <label htmlFor={fieldId} className="btn btn-sm btn-outline-primary mt-1 mb-0">
            <FontAwesomeIcon icon={faCloudUploadAlt} className="mr-1" />
            {uploadedFileName ? "Change File" : "Choose File"}
          </label>
        </div>
      </div>

      <input type="file" id={fieldId} className="d-none" accept="image/*,video/*" onChange={handleFileChange} />

      {uploadError && (
        <div className="text-danger mt-2" style={{ fontSize: ".8rem" }}>
          {uploadError}
        </div>
      )}
      {recommendedResolution && <div className="text-danger mt-1" style={{ fontSize: ".8rem" }}>Recommended Resolution: {recommendedResolution}</div>}

      {/* Bootstrap Modal */}
      <div className={`modal fade ${showModal ? "show d-block" : ""}`} tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Crop Image</h5>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)}>X</button>
            </div>
            <div className="modal-body" style={{ position: "relative", height: "400px", background: "#333" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={minZoom}
                  maxZoom={3}
                  aspect={aspectRatio}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="rect"
                  showGrid={true}
                  objectFit="cover"
                  style={{
                    containerStyle: {
                      backgroundColor: "#ccc" // light grey instead of black
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center">
              <input
                type="range"
                className="form-range w-50"
                min={minZoom}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
              <div>
                <button type="button" className="btn btn-secondary me-2" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={showCroppedImage}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageUploaderWithCrop;
